import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(
  amount: number,
  currency: string = "AUD",
  locale: string = "en-AU"
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(date: string | Date, format: string = "D MMM YYYY"): string {
  return dayjs(date).format(format);
}

export function formatRelativeDate(date: string | Date): string {
  return dayjs(date).fromNow();
}

export function daysUntil(date: string | Date): number {
  const target = dayjs(date);
  const now = dayjs();
  const diff = target.diff(now, "day");
  return Math.max(0, diff);
}

export function getInitials(name: string | null | undefined): string {
  if (!name) return "U";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + "...";
}

export function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function getOccasionColor(occasion: string | undefined | null): string {
  const colors: Record<string, string> = {
    birthday: "bg-gift-pink-500",
    christmas: "bg-red-500",
    valentine: "bg-gift-pink-600",
    anniversary: "bg-gift-purple-500",
    wedding: "bg-gift-amber-500",
    graduation: "bg-blue-500",
    "baby-shower": "bg-pink-400",
    "mothers-day": "bg-gift-pink-400",
    "fathers-day": "bg-blue-600",
    housewarming: "bg-orange-500",
    retirement: "bg-gift-amber-600",
    "thank-you": "bg-green-500",
    "just-because": "bg-gift-purple-400",
    other: "bg-gray-500",
  };
  if (!occasion) return colors.other;
  return colors[occasion.toLowerCase()] || colors.other;
}

/** Returns an estimated default age offset (years ago) based on relationship type */
export function getDefaultAgeByRelationship(relationship: string): number {
  const map: Record<string, number> = {
    parent: 55,
    grandparent: 75,
    child: 8,
    sibling: 25,
    friend: 25,
    colleague: 30,
    boss: 40,
    wife: 30,
    husband: 30,
    partner: 28,
    girlfriend: 25,
    boyfriend: 25,
    "in-law": 55,
    "aunt/uncle": 50,
    cousin: 25,
    neighbor: 35,
    teacher: 40,
    client: 35,
  };
  return map[relationship.toLowerCase()] ?? 20;
}

const ZODIAC_SIGNS = [
  { sign: "Capricorn", symbol: "\u2651", start: [1, 1], end: [1, 19] },
  { sign: "Aquarius", symbol: "\u2652", start: [1, 20], end: [2, 18] },
  { sign: "Pisces", symbol: "\u2653", start: [2, 19], end: [3, 20] },
  { sign: "Aries", symbol: "\u2648", start: [3, 21], end: [4, 19] },
  { sign: "Taurus", symbol: "\u2649", start: [4, 20], end: [5, 20] },
  { sign: "Gemini", symbol: "\u264A", start: [5, 21], end: [6, 20] },
  { sign: "Cancer", symbol: "\u264B", start: [6, 21], end: [7, 22] },
  { sign: "Leo", symbol: "\u264C", start: [7, 23], end: [8, 22] },
  { sign: "Virgo", symbol: "\u264D", start: [8, 23], end: [9, 22] },
  { sign: "Libra", symbol: "\u264E", start: [9, 23], end: [10, 22] },
  { sign: "Scorpio", symbol: "\u264F", start: [10, 23], end: [11, 21] },
  { sign: "Sagittarius", symbol: "\u2650", start: [11, 22], end: [12, 21] },
  { sign: "Capricorn", symbol: "\u2651", start: [12, 22], end: [12, 31] },
] as const;

/** Returns the zodiac sign and symbol for a given date */
export function getZodiacSign(date: string | Date): { sign: string; symbol: string } | null {
  const d = dayjs(date);
  if (!d.isValid()) return null;
  const month = d.month() + 1; // 1-based
  const day = d.date();
  for (const z of ZODIAC_SIGNS) {
    const [sm, sd] = z.start;
    const [em, ed] = z.end;
    if ((month === sm && day >= sd) || (month === em && day <= ed)) {
      return { sign: z.sign, symbol: z.symbol };
    }
  }
  return { sign: "Capricorn", symbol: "\u2651" };
}

export function getOccasionEmoji(occasion: string | undefined | null): string {
  const emojis: Record<string, string> = {
    birthday: "🎂",
    christmas: "🎄",
    valentine: "💝",
    anniversary: "💍",
    wedding: "💒",
    graduation: "🎓",
    "baby-shower": "🍼",
    "mothers-day": "🌸",
    "fathers-day": "👔",
    housewarming: "🏠",
    retirement: "🎉",
    "thank-you": "🙏",
    "just-because": "💫",
    easter: "🐣",
    halloween: "🎃",
    "new-year": "🎆",
    engagement: "💎",
  };
  if (!occasion) return "🎁";
  return emojis[occasion.toLowerCase()] || "🎁";
}
